import { useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FormProvider, RHFDateRangePickerField, RHFMultipleSelectField, RHFSearchableSelectField, RHFSelectField, RHFTextField } from './hook-form';
import { ALL_SERVICE_TYPES, ALL_SERVICE_TYPES_OPTION, COURIER_LIST_QUERY_PAYLOAD, CourierFilter, MerchantFilterCached } from './ProshipFormComponents';
import { useGetAllCourierPartnersCachedQuery } from '../redux/commonApiCalls';

// ─── Styled components ────────────────────────────────────────────────────────

// Structural pieces — exported so pages with custom filters (fields FilterBar
// doesn't support) can compose their own bar in the exact same look.
export const FilterCard = styled(Card)({
    marginBottom: 16,
    boxShadow: 'none',
    borderRadius: 8,
});

export const FilterCardContent = styled(CardContent)({
    padding: '16px !important',
    '&:last-child': { paddingBottom: '16px !important' },
});

export const FilterRow = styled(Box)({
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'flex-end',
});

// Static label above each filter field — exported so other pages can use the
// same label style above custom fields rendered outside FilterBar.
export const FieldLabel = styled(Typography)({
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#888',
    fontWeight: 600,
    marginBottom: 4,
    display: 'block',
    lineHeight: 1.2,
});

// All MUI input overrides in one reusable object — 33px uniform height,
// hidden floating label, plain #ccc border, forced placeholder opacity.
// Shared by FilterField and FilterDateField so they stay in sync.
const inputOverrides = {
    '& .MuiOutlinedInput-root': {
        fontSize: '12px',
        backgroundColor: '#fff',
        borderRadius: 4,
        minHeight: 33,
        height: 33,
    },
    '& .MuiOutlinedInput-input': {
        padding: '8px 10px',
        fontSize: '12px',
        color: '#333',
        height: 'auto',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#ccc',
        top: 0,
        '& legend': { display: 'none' },
    },
    '& .MuiInputLabel-root': { display: 'none' },
    '& .MuiFormHelperText-root': { display: 'none' },
    '& input::placeholder, & .MuiInputBase-input::placeholder': {
        opacity: '1 !important',
        color: '#999',
        fontSize: '12px',
    },
    '& .MuiOutlinedInput-input::placeholder': {
        opacity: '1 !important',
        color: '#999',
    },
    // fontSize only — leave color to the Typography's own sx so selected
    // values render dark and placeholders render muted (each sets its own).
    '& .MuiSelect-select .MuiTypography-root': { fontSize: '12px' },
    '& .MuiSelect-select': {
        padding: '8px 32px 8px 10px !important',
        minHeight: '0 !important',
        height: 'auto',
        lineHeight: '17px',
        boxSizing: 'border-box',
    },
    '& .MuiAutocomplete-inputRoot': { padding: '0 35px 0 4px !important' },
    '& .MuiAutocomplete-input': { padding: '8px 10px !important' },
};

// Standard filter field — stretches but never collapses below 140px.
// Exported so pages can wrap custom fields in the same styled slot.
export const FilterField = styled(Box)({
    flex: '1 1 140px',
    minWidth: 140,
    ...inputOverrides,
});

// Wider slot for the date range picker (needs room for two dates).
export const FilterDateField = styled(Box)({
    flex: '0 0 260px',
    minWidth: 260,
    ...inputOverrides,
});

export const FilterActions = styled(Box)({
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flex: '0 0 auto',
});

export const ApplyButton = styled(Button)({
    backgroundColor: '#1565c0 !important',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '8px 20px',
    boxShadow: 'none',
    '&:hover': {
        backgroundColor: '#0d47a1 !important',
        boxShadow: 'none',
    },
});

export const ResetButton = styled(Button)({
    backgroundColor: '#ef4444 !important',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '8px 20px',
    boxShadow: 'none',
    '&:hover': {
        backgroundColor: '#dc2626 !important',
        boxShadow: 'none',
    },
});

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_ZONE_OPTIONS = ['LOCAL', 'REGIONAL', 'METROS', 'ROI', 'SPECIAL']
    .map((v) => ({ label: _.startCase(v), value: v }));

const PAYMENT_MODE_OPTIONS = [
    { label: 'COD', value: 'COD' },
    { label: 'Prepaid', value: 'PREPAID' },
];

const EMPTY_DEFAULTS = {
    merchant: '',
    courier: '',
    awb: '',
    reference: '',
    rtoAwb: '',
    city: '',
    state: '',
    pincode: '',
    pickupPincode: '',
    fromDate: '',
    toDate: '',
    status: [],
    zone: [],
    paymentMode: '',
    attempts: '',
    missCategory: '',
    parent: '',
    serviceType: [],
    accountCode: [],
};

// ─── FilterBar ────────────────────────────────────────────────────────────────

/**
 * Generic reusable filter bar.
 *
 * Props:
 *   show            string[]   — field keys to render, in order
 *   onApply         fn         — called with flat filter object on submit
 *   onReset         fn         — called after internal form reset
 *   initialValues   object     — override form defaults (e.g. pre-set dates)
 *   statusOptions   array      — { label, value }[] for 'status' multi-select
 *   courierType     string     — 'B2C' | 'B2B' | null  (passed to CourierFilter)
 *   zoneOptions     array      — override default zone options
 *   attemptsOptions array      — { label, value }[] for 'attempts' select
 *   missCatOptions  array      — { label, value }[] for 'missCategory' select
 *
 * Supported show keys:
 *   merchant | courier | awb | reference | rtoAwb | city | state | pincode |
 *   pickupPincode | status | zone | paymentMode | attempts | missCategory |
 *   dateRange | parent | serviceType | accountCode
 *
 * parent + serviceType + accountCode behave as a chained filter (replacement
 * for the old single-step `courier`):
 *   - All three hide whenever `awb` has any value
 *   - `parent`      — single select, distinct courier-partner parents
 *   - `serviceType` — MULTI select, distinct serviceTypePrimary/Secondary
 *                     values across the selected parent's couriers; hidden
 *                     until a parent is picked
 *   - `accountCode` — MULTI select (OPTIONAL), distinct cpAccountCode values
 *                     across the couriers matching parent + serviceType;
 *                     hidden until parent + at least one serviceType picked.
 *                     If left empty, every matching account code is used.
 *   - The consuming page resolves the chain to the final set of courier IDs
 *     and pushes them via `{field: "courier.$id", operator: "in", value: [...]}`
 */
export default function FilterBar({
    show = [],
    onApply,
    onReset,
    initialValues = {},
    statusOptions = [],
    courierType = 'B2C',
    zoneOptions = DEFAULT_ZONE_OPTIONS,
    attemptsOptions = [],
    missCatOptions = [],
}) {
    const defaultValues = { ...EMPTY_DEFAULTS, ...initialValues };
    const methods = useForm({ defaultValues });
    const { handleSubmit, reset, control } = methods;
    const { enqueueSnackbar } = useSnackbar();

    const v = (key) => show.includes(key);

    // ── Parent + Service Type + Account Code chained filter ───────────────────
    // These three together replace the old single-step Courier filter on
    // Forward / Reverse Shipments. The consuming page resolves the chain to
    // the matching courier-partner IDs and sends them as a `courier.$id in
    // [...]` clause.
    //   - All three hide when AWB has any value
    //   - serviceType stays hidden until a parent is picked
    //   - accountCode stays hidden until both parent + at least one service
    //     type are picked
    //   - parent + serviceType are required (their labels carry a red `*`),
    //     accountCode is optional (no `*`); if accountCode is left empty,
    //     every matching account code under parent + serviceType is used.
    //   - Filter button is NOT disabled; the consuming page just skips the
    //     courier-related clause when either required half is missing.
    const awbValue = useWatch({ control, name: 'awb' });
    const parentValue = useWatch({ control, name: 'parent' });
    const serviceTypeValue = useWatch({ control, name: 'serviceType' });
    const showParent = v('parent') && !awbValue;
    const showServiceType = v('serviceType') && !awbValue && !!parentValue;
    const showAccountCode = v('accountCode') && !awbValue && !!parentValue && Array.isArray(serviceTypeValue) && serviceTypeValue.length > 0;

    const handleReset = () => {
        reset(defaultValues);
        onReset?.();
    };

    return (
        <FilterCard>
            <FilterCardContent>
                <FormProvider methods={methods} onSubmit={handleSubmit((data) => {
                    // Validation: when a Courier (parent) is picked, the user
                    // MUST also pick at least one Service Type. Show an error
                    // toast instead of silently dropping the courier filter.
                    if (data.parent && (!Array.isArray(data.serviceType) || data.serviceType.length === 0)) {
                        enqueueSnackbar('Please select at least one Service Type for the chosen Courier.', { variant: 'error' });
                        return;
                    }
                    onApply?.(data);
                })}>
                    <FilterRow>

                        {v('merchant') && (
                            <FilterField>
                                <FieldLabel>Merchant</FieldLabel>
                                <MerchantFilterCached fullWidth placeholder="Select Merchant" />
                            </FilterField>
                        )}

                        {v('courier') && (
                            <FilterField>
                                <FieldLabel>Courier</FieldLabel>
                                <CourierFilter filterList={courierType} placeholder="Select Courier" />
                            </FilterField>
                        )}

                        {showParent && (
                            <ParentFilterField courierType={courierType} />
                        )}

                        {showServiceType && (
                            <ServiceTypeFilterField parent={parentValue} courierType={courierType} />
                        )}

                        {showAccountCode && (
                            <AccountCodeFilterField
                                parent={parentValue}
                                serviceTypes={serviceTypeValue}
                                courierType={courierType}
                            />
                        )}

                        {v('reference') && (
                            <FilterField>
                                <FieldLabel>Order Number</FieldLabel>
                                <RHFTextField name="reference" label="" placeholder="Order Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>
                        )}

                        {v('awb') && (
                            <FilterField>
                                <FieldLabel>AWB Number</FieldLabel>
                                <RHFTextField name="awb" label="" placeholder="AWB Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>
                        )}

                        {v('rtoAwb') && (
                            <FilterField>
                                <FieldLabel>RTO AWB</FieldLabel>
                                <RHFTextField name="rtoAwb" label="" placeholder="RTO AWB" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>
                        )}

                        {v('city') && (
                            <FilterField>
                                <FieldLabel>City</FieldLabel>
                                <RHFTextField name="city" label="" placeholder="City" fullWidth size="small" InputLabelProps={{ shrink: true }} disabled />
                            </FilterField>
                        )}

                        {v('state') && (
                            <FilterField>
                                <FieldLabel>State</FieldLabel>
                                <RHFTextField name="state" label="" placeholder="State" fullWidth size="small" InputLabelProps={{ shrink: true }} disabled />
                            </FilterField>
                        )}

                        {v('pincode') && (
                            <FilterField>
                                <FieldLabel>Pincode</FieldLabel>
                                <RHFTextField name="pincode" label="" placeholder="Pincode" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>
                        )}

                        {v('pickupPincode') && (
                            <FilterField>
                                <FieldLabel>Pickup Pincode</FieldLabel>
                                <RHFTextField name="pickupPincode" label="" placeholder="Pickup Pincode" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>
                        )}

                        {v('status') && statusOptions.length > 0 && (
                            <FilterField>
                                <FieldLabel>Order Status</FieldLabel>
                                <RHFMultipleSelectField name="status" label="" placeholder="Select Order Status" width="100%" options={_.orderBy(statusOptions, 'value')} outputFormat="array" size="small" search />
                            </FilterField>
                        )}

                        {v('zone') && (
                            <FilterField>
                                <FieldLabel>Zone</FieldLabel>
                                <RHFMultipleSelectField name="zone" label="" placeholder="Select Zone" width="100%" options={_.orderBy(zoneOptions, 'value')} outputFormat="array" size="small" />
                            </FilterField>
                        )}

                        {v('paymentMode') && (
                            <FilterField>
                                <FieldLabel>Payment Mode</FieldLabel>
                                <RHFSelectField name="paymentMode" label="" placeholder="All" width="100%" size="small" displayEmpty options={PAYMENT_MODE_OPTIONS} />
                            </FilterField>
                        )}

                        {v('attempts') && attemptsOptions.length > 0 && (
                            <FilterField>
                                <FieldLabel>Attempts</FieldLabel>
                                <RHFSelectField name="attempts" label="" placeholder="All" width="100%" size="small" displayEmpty options={attemptsOptions} />
                            </FilterField>
                        )}

                        {v('missCategory') && missCatOptions.length > 0 && (
                            <FilterField>
                                <FieldLabel>Miss Category</FieldLabel>
                                <RHFSelectField name="missCategory" label="" placeholder="All" width="100%" size="small" displayEmpty options={missCatOptions} />
                            </FilterField>
                        )}

                        {v('dateRange') && (
                            <FilterDateField>
                                <FieldLabel>Date Range</FieldLabel>
                                <RHFDateRangePickerField name="fromDate" label="" placeholder="Select Date Range" size="small" maxDate={new Date()} />
                            </FilterDateField>
                        )}

                        <FilterActions>
                            <ApplyButton type="submit" variant="contained" size="small">
                                Filter
                            </ApplyButton>
                            <ResetButton type="button" variant="contained" size="small" onClick={handleReset}>
                                Reset
                            </ResetButton>
                        </FilterActions>

                    </FilterRow>
                </FormProvider>
            </FilterCardContent>
        </FilterCard>
    );
}

// ─── Helpers shared by Parent + ServiceType subcomponents ────────────────────
// scopeCouriersByPageType mirrors CourierFilter's own filtering logic so the
// Parent / ServiceType dropdowns only consider couriers relevant to the page
// the filter bar is mounted on (e.g. only RVP couriers on Reverse Shipments).
const scopeCouriersByPageType = (couriers, courierType) => {
    if (!Array.isArray(couriers)) return [];
    if (!courierType) return couriers;
    return couriers.filter((c) => c?.supported_shipment === courierType || c?.supportedShipmentType === courierType);
};

// ─── Parent (courier brand) sub-field ────────────────────────────────────────
// Distinct parent names from the cached courier list, scoped to the page type.
function ParentFilterField({ courierType }) {
    const { data: couriers = [] } = useGetAllCourierPartnersCachedQuery(COURIER_LIST_QUERY_PAYLOAD);

    const options = useMemo(() => {
        const scoped = scopeCouriersByPageType(couriers, courierType);
        return Array.from(new Set(scoped.map((c) => c?.parent).filter(Boolean)))
            .sort()
            .map((p) => ({ label: p, value: p }));
    }, [couriers, courierType]);

    return (
        <FilterField>
            <FieldLabel>Courier</FieldLabel>
            <RHFSearchableSelectField
                name="parent"
                label=""
                placeholder={options.length > 0 ? 'Select Courier' : 'No couriers available'}
                fullWidth
                options={options}
            />
        </FilterField>
    );
}

// ─── Service Type sub-field (MULTI-SELECT) ───────────────────────────────────
// Rendered only after a parent is picked. Options = "All Service Types" plus
// distinct serviceTypePrimary AND serviceTypeSecondary values across that
// parent's account codes, scoped to the page type (so e.g. Reverse Shipments
// doesn't list forward-only types). "All Service Types" lets the user opt out
// of the service-type narrowing entirely — useful when the parent has no
// service types configured at all (only "All" appears) or when the user wants
// to consider every service type under the parent.
function ServiceTypeFilterField({ parent, courierType }) {
    const { data: couriers = [] } = useGetAllCourierPartnersCachedQuery(COURIER_LIST_QUERY_PAYLOAD);
    const { setValue } = useFormContext();
    const serviceTypeValue = useWatch({ name: 'serviceType' });

    const options = useMemo(() => {
        if (!parent) return [];
        const scoped = scopeCouriersByPageType(couriers, courierType).filter((c) => c?.parent === parent);
        const types = new Set();
        scoped.forEach((c) => {
            if (c?.serviceTypePrimary) types.add(c.serviceTypePrimary);
            if (c?.serviceTypeSecondary) types.add(c.serviceTypeSecondary);
        });
        const specific = Array.from(types).sort().map((t) => ({ label: t, value: t }));
        return [ALL_SERVICE_TYPES_OPTION, ...specific];
    }, [couriers, parent, courierType]);

    // ── "Select All" toggle invariant ──────────────────────────────────────
    // When the "All Service Types" sentinel is in the selected array we keep
    // every specific option also in the array — so each individual dropdown
    // item renders as checked (MUI Select-multiple highlights anything in the
    // value list). Three transitions we react to:
    //   1. User just CHECKED "All"    → fill in every specific value too
    //   2. User just UNCHECKED "All"  → clear specifics too (full reset)
    //   3. "All" still checked but a specific got unchecked → drop "All"
    //      from the array (the state is no longer "all").
    const prevRef = useRef(serviceTypeValue);
    useEffect(() => {
        const prev = Array.isArray(prevRef.current) ? prevRef.current : [];
        const curr = Array.isArray(serviceTypeValue) ? serviceTypeValue : [];
        const specificValues = options.filter((o) => o.value !== ALL_SERVICE_TYPES).map((o) => o.value);
        const prevHasAll = prev.includes(ALL_SERVICE_TYPES);
        const currHasAll = curr.includes(ALL_SERVICE_TYPES);

        // 1) "All" just checked — fill in every specific so they all appear checked
        if (!prevHasAll && currHasAll) {
            const next = [ALL_SERVICE_TYPES, ...specificValues];
            prevRef.current = next;
            setValue('serviceType', next, { shouldDirty: true });
            return;
        }

        // 2) "All" just unchecked — clear everything
        if (prevHasAll && !currHasAll) {
            prevRef.current = [];
            setValue('serviceType', [], { shouldDirty: true });
            return;
        }

        // 3) "All" still checked but a specific got removed — drop "All" too
        if (prevHasAll && currHasAll && specificValues.length > 0) {
            const allSpecificsChecked = specificValues.every((v) => curr.includes(v));
            if (!allSpecificsChecked) {
                const next = curr.filter((v) => v !== ALL_SERVICE_TYPES);
                prevRef.current = next;
                setValue('serviceType', next, { shouldDirty: true });
                return;
            }
        }

        prevRef.current = curr;
    }, [serviceTypeValue, options, setValue]);

    return (
        <FilterField>
            <FieldLabel>
                Service Type <span style={{ color: '#dc2626' }}>*</span>
            </FieldLabel>
            <RHFMultipleSelectField
                name="serviceType"
                label=""
                placeholder={options.length > 0 ? 'Select Service Type' : 'No service type configured'}
                width="100%"
                size="small"
                outputFormat="array"
                search
                options={options}
            />
        </FilterField>
    );
}

// ─── Account Code sub-field (MULTI-SELECT, OPTIONAL) ─────────────────────────
// Rendered only after parent + at least one service type are picked. Options =
// distinct cpAccountCode values across the couriers matching the chain so far.
// If user leaves it empty, the consuming page falls back to "all matching".
// When serviceTypes includes ALL_SERVICE_TYPES, every account code under the
// parent is offered (no service-type narrowing).
function AccountCodeFilterField({ parent, serviceTypes, courierType }) {
    const { data: couriers = [] } = useGetAllCourierPartnersCachedQuery(COURIER_LIST_QUERY_PAYLOAD);

    const options = useMemo(() => {
        if (!parent || !Array.isArray(serviceTypes) || serviceTypes.length === 0) return [];
        const includesAll = serviceTypes.includes(ALL_SERVICE_TYPES);
        const scoped = scopeCouriersByPageType(couriers, courierType).filter((c) => {
            if (c?.parent !== parent) return false;
            if (includesAll) return true;
            const primaryHit = c?.serviceTypePrimary && serviceTypes.includes(c.serviceTypePrimary);
            const secondaryHit = c?.serviceTypeSecondary && serviceTypes.includes(c.serviceTypeSecondary);
            return primaryHit || secondaryHit;
        });
        const codes = new Set(scoped.map((c) => c?.cpAccountCode).filter(Boolean));
        return Array.from(codes).sort().map((code) => ({ label: code, value: code }));
    }, [couriers, parent, serviceTypes, courierType]);

    return (
        <FilterField>
            <FieldLabel>Account Code</FieldLabel>
            <RHFMultipleSelectField
                name="accountCode"
                label=""
                placeholder={options.length > 0 ? 'All matching (optional)' : 'No account codes'}
                width="100%"
                size="small"
                outputFormat="array"
                search
                options={options}
            />

        </FilterField>
    );
}
